import { Component, OnInit } from '@angular/core';
import { BreadcrumbComponent } from "@shared/components/breadcrumb/breadcrumb.component";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { UserRole } from "@core/models/Enums/UserRole";
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from "@angular/material/autocomplete";
import { MatSelect } from "@angular/material/select";
import { NgClass, NgForOf, NgIf } from "@angular/common";
import { UserDetails } from "@core/models/UserDetails";
import { Doctor } from "@core/models/Doctor";
import { BusinessGroup } from "@core/models/BusinessGroup";
import { Branch } from "@core/models/Branch";
import { CatalogsService } from "@core/http/catalogs.service";
import { DefaultResponse } from "@core/models/Http/DefaultResponse";
import { FileUploadComponent } from "@shared/components/file-upload/file-upload.component";
import { ActivatedRoute, Router } from "@angular/router";
import { DoctorDetails } from "@core/models/DoctorDetails";
import { Settlement } from "@core/models/Settlement";
import { debounceTime, distinctUntilChanged, forkJoin, of, switchMap, tap } from "rxjs";
import { catchError } from "rxjs/operators";
import { CreateUserResponse } from "@core/models/Http/Response/CreateUserResponse";
import { ImageSignature } from "@core/models/ImageSignature";
import { HttpMesageHandlerService } from "@core/service/http-mesage-handler";
import { HttpErrorResponse } from "@angular/common/http";
import { REGEX } from "@shared/utils/regex.constants";
import { MsgResponse } from "@core/models/MsgResponse";
import Swal from "sweetalert2";
import { NgxMatIntlTelInputComponent } from "ngx-mat-intl-tel-input";
import { AuthManagementService } from "@core/service/auth-management.service";
import { environment } from "@enviroment/environment";
import { DoctorService } from "@core/http/table-data-services/Doctor.service";
import { UserService } from "@core/http/table-data-services/User.service";
import { MatCheckboxModule } from '@angular/material/checkbox';
import {RegionWordPipe} from "@core/pipes/region-word.pipe";
import {regionWord} from "@shared/utils/region-word.util";

type ColegiadoOption = { id: number; label: string };

@Component({
  selector: 'app-doctor-edit-create',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    FormsModule,
    MatButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    NgForOf,
    FileUploadComponent,
    ReactiveFormsModule,
    NgClass,
    NgIf,
    NgxMatIntlTelInputComponent,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatCheckboxModule,
    RegionWordPipe
  ],
  templateUrl: './doctor-edit-create.component.html',
  styleUrl: './doctor-edit-create.component.scss'
})
export class DoctorEditCreateComponent implements OnInit {
  param: string | null = null;
  private idTipoUsuarioMedico: string | null = null;
  private idTipoUsuarioParticular: string | null = null;

  UserRole = UserRole;
  user: UserDetails;
  doctor: Doctor;
  archive: ImageSignature;

  userImagen: string | null = null;
  firmaImagen: string | null = null;
  private readonly DOCTOR_FALLBACK = 'assets/images/doctor.png';

  businessGroups: BusinessGroup[] = [];
  branches: Branch[] = [];

  settlements: Settlement[] = [];
  settlementSearch = '';

  userForm: UntypedFormGroup;
  doctorForm: UntypedFormGroup;
  archiveForm: UntypedFormGroup;
  isAdmin: boolean;

  breadcrumbItems = [
    { label: 'MEDICOS', link: '/doctor/doctor-list' },
    { label: 'NUEVO MEDICO' }
  ];

  showEmpresaSection = true;
  showGempSelect = true;
  showSucursalSelect = true;
  private idUsuarioActual: string | null = null;

  preferredCountries: string[] = ['mx', 'es', 'us'];

  // ✅ REGION / TIPO USUARIO
  isRegionES = false;
  isRegionMX = false;
  showParticularToggle = false;
  isParticularSelected = false;

  colegiadoOptions: ColegiadoOption[] = [
    { id: 1, label: 'MEDICINA GENERAL' },
    { id: 2, label: 'DENTISTA' },
    { id: 3, label: 'PODOLOGIA' },
  ];

  constructor(
    private catalogsService: CatalogsService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private doctorService: DoctorService,
    private httpMsgHandler: HttpMesageHandlerService,
    private authManagement: AuthManagementService,
    private userService: UserService,
  ) {
    this.user = new UserDetails({} as UserDetails);
    this.doctor = new Doctor({} as Doctor);
    this.archive = new ImageSignature({} as ImageSignature);
    this.isAdmin = this.authManagement.isUserAdmin();

    this.route.paramMap.subscribe(params => {
      const param = params.get('param');
      this.param = param ? atob(param) : null;
    });

    this.userForm = this.createUserForm(false);
    this.doctorForm = this.createDoctorForm();
    this.archiveForm = this.createArchivesForm();

    const r = (environment as any)?.region?.toString().toUpperCase();
    this.isRegionES = r === 'ES';
    this.isRegionMX = r === 'MX';
    this.preferredCountries = this.isRegionES ? ['es', 'mx', 'us'] : ['mx', 'es', 'us'];

    // ✅ ES siempre será PARTICULAR
    if (this.isRegionES) {
      this.isParticularSelected = true;
    }
  }

  ngOnInit(): void {
    this.configureByRole();
    this.configureTipoUsuarioByRegionAndRole();

    this.userService.getTiposUsuario().subscribe({
      next: (resp: DefaultResponse<any[]>) => {
        const tipos = resp.data || [];

        const medico = tipos.find(t =>
          String(t.nombre || '').trim().toLowerCase() === 'medico'
        );

        const particular = tipos.find(t =>
          String(t.nombre || '').trim().toLowerCase() === 'particular'
        );

        this.idTipoUsuarioMedico = medico ? String(medico.idTipoUsuario) : null;
        this.idTipoUsuarioParticular = particular ? String(particular.idTipoUsuario) : null;
      },
      error: (err) => this.httpMsgHandler.handleMsg(err),
    });

    if (this.param) {
      this.getDoctorDetails(this.param);
      this.setBreadcrumb();
    }

    this.userForm.get('settlementSearch')?.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((value: any) => {
          if (typeof value === 'string' && /^\d{5}$/.test(value)) {
            this.searchSettlements(value);
          } else if (!value || typeof value === 'string') {
            this.settlements = [];
          }
        })
      )
      .subscribe();

    this.wireMedicoToUsuarioSync();
  }

  /**
   * ✅ MX + ADMIN => puede elegir PARTICULAR
   * ✅ MX + no admin => siempre MEDICO
   * ✅ ES => siempre PARTICULAR
   */
  private configureTipoUsuarioByRegionAndRole(): void {
    if (this.isRegionES) {
      this.showParticularToggle = false;
      this.isParticularSelected = true;
      return;
    }

    if (this.isRegionMX && this.isAdmin) {
      this.showParticularToggle = true;
      this.isParticularSelected = false;
      return;
    }

    this.showParticularToggle = false;
    this.isParticularSelected = false;
  }

  /**
   * ✅ Regresa el idTipoUsuario correcto según región y toggle
   */
  private getSelectedTipoUsuarioId(): string | null {
    // ES => siempre PARTICULAR
    if (this.isRegionES) {
      return this.idTipoUsuarioParticular;
    }

    // MX + admin + toggle activado => PARTICULAR
    if (this.isRegionMX && this.showParticularToggle && this.isParticularSelected) {
      return this.idTipoUsuarioParticular;
    }

    // default => MEDICO
    return this.idTipoUsuarioMedico;
  }

  private wireMedicoToUsuarioSync(): void {
    this.doctorForm.get('cedulaGeneral')?.valueChanges
      .pipe(debounceTime(150), distinctUntilChanged())
      .subscribe((v: any) => {
        const ced = (v ?? '').toString().trim();
        if (this.userForm.get('cedula')?.value !== ced) {
          this.userForm.patchValue({ cedula: ced }, { emitEvent: false });
        }
      });

    this.doctorForm.get('colegiado')?.valueChanges
      .pipe(debounceTime(150), distinctUntilChanged())
      .subscribe((v: any) => {
        const valNum = (v === null || v === undefined || v === '' || v === 0 || v === '0')
          ? null
          : Number(v);

        if (this.userForm.get('idColegiado')?.value !== valNum) {
          this.userForm.patchValue({ idColegiado: valNum }, { emitEvent: false });
        }
      });
  }

  private cleanBlobString(v: string | null | undefined): string {
    if (!v) return '';
    return String(v).replace(/^<Blob>/i, '').replace(/<\/Blob>$/i, '').trim();
  }

  private resolveImageAnyToDataUrl(v: any, contentType = 'image/png'): string {
    if (!v) return this.DOCTOR_FALLBACK;

    if (typeof v === 'string') {
      const s0 = v.trim();
      if (!s0 || s0 === 'null' || s0 === 'undefined') return this.DOCTOR_FALLBACK;
      if (s0.startsWith('data:image/')) return s0;
      if (s0.startsWith('http') || s0.startsWith('assets/')) return s0;

      const s = this.cleanBlobString(s0);

      try {
        atob(s);
        return `data:${contentType};base64,${s}`;
      } catch {
        return this.DOCTOR_FALLBACK;
      }
    }

    const raw = v.dataUrl ?? v.dataURL ?? v.base64 ?? '';
    if (!raw) return this.DOCTOR_FALLBACK;
    if (typeof raw === 'string' && raw.startsWith('data:image/')) return raw;

    try {
      atob(raw);
      return `data:${contentType};base64,${raw}`;
    } catch {
      return this.DOCTOR_FALLBACK;
    }
  }

  get resolvedPanelImage(): string {
    const raw = this.archiveForm?.get('imagen')?.value ?? this.userImagen ?? '';
    return this.resolveImageAnyToDataUrl(raw);
  }

  get resolvedSignatureImage(): string | null {
    const raw = this.archiveForm?.get('firma')?.value ?? this.firmaImagen ?? '';
    const val = this.resolveImageAnyToDataUrl(raw);
    return val === this.DOCTOR_FALLBACK ? null : val;
  }

  private setBreadcrumb(): void {
    this.breadcrumbItems = [
      { label: 'MEDICOS', link: '/doctor/doctor-list' },
      { label: this.param ? 'EDITAR MEDICO' : 'NUEVO MEDICO' }
    ];
  }

  private configureByRole(): void {
    const u = this.authManagement.userData();
    const isSupervisor = this.authManagement.isUserPharmacySupervision();
    const isResponsable = this.authManagement.isUserPharmacyResponsible();

    if (isResponsable) {
      this.showEmpresaSection = false;
      this.showGempSelect = false;
      this.showSucursalSelect = false;

      this.userForm.patchValue({
        idGEMP: u.IdGEMP || null,
        idSucursal: u.IdSucursal || null
      }, { emitEvent: false });
      return;
    }

    if (isSupervisor) {
      this.showEmpresaSection = true;
      this.showGempSelect = false;
      this.showSucursalSelect = true;

      this.userForm.patchValue({ idGEMP: u.IdGEMP || null }, { emitEvent: false });
      if (u.IdGEMP) this.getBranchesByGemp(String(u.IdGEMP));
      return;
    }

    this.showEmpresaSection = true;
    this.showGempSelect = true;
    this.showSucursalSelect = true;
    this.getBusinessGroups();
  }

  getBusinessGroups() {
    this.catalogsService.getBusinessGroups().subscribe({
      next: (res: DefaultResponse<BusinessGroup[]>) => {
        this.businessGroups = [...res.data];
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  getDoctorDetails(param: string) {
    this.doctorService.getDoctorByIdDoctor(param).subscribe({
      next: (res: DefaultResponse<DoctorDetails>) => {
        this.settlementSearch = res.data.asentamiento || '';
        if (this.settlementSearch) this.searchSettlements(this.settlementSearch);

        this.user = this.mapToUserDetails(res.data);
        this.doctor = this.mapToDoctor(res.data);

        this.userForm = this.createUserForm(true);
        this.doctorForm = this.createDoctorForm();

        this.wireMedicoToUsuarioSync();

        const idUsuario = String(res.data.idUsuario || '').trim();

        const parseAsset = (resp: any): { base64: string; contentType: string } => {
          if (!resp) return { base64: '', contentType: 'image/jpeg' };

          const maybeData = resp?.data ?? resp;

          if (typeof maybeData === 'object' && maybeData?.base64) {
            const b64 = String(maybeData.base64 || '').trim();
            const ct = String(maybeData.contentType || 'image/jpeg').trim();
            return { base64: b64, contentType: ct };
          }

          if (typeof maybeData === 'string') {
            const cleaned = this.cleanBlobString(maybeData).trim();
            return { base64: cleaned, contentType: 'image/jpeg' };
          }

          return { base64: '', contentType: 'image/jpeg' };
        };

        const toDataUrl = (b64: string, contentType: string) =>
          b64 ? `data:${contentType || 'image/jpeg'};base64,${b64}` : null;

        if (idUsuario) {
          forkJoin({
            img: this.userService.getUsuarioAsset(idUsuario, 1).pipe(catchError(() => of(null))),
            firma: this.userService.getUsuarioAsset(idUsuario, 2).pipe(catchError(() => of(null))),
          }).subscribe(({ img, firma }) => {
            const imgParsed = parseAsset(img);
            const firmaParsed = parseAsset(firma);

            this.idUsuarioActual = String(res.data.idUsuario || null);

            this.userImagen = toDataUrl(imgParsed.base64, imgParsed.contentType);
            this.firmaImagen = toDataUrl(firmaParsed.base64, firmaParsed.contentType);

            this.archiveForm.patchValue(
              { imagen: null, firma: null },
              { emitEvent: false }
            );
          });
        } else {
          this.userImagen = null;
          this.firmaImagen = null;
          this.archiveForm.patchValue({ imagen: null, firma: null }, { emitEvent: false });
        }

        const u = this.authManagement.userData();
        if (this.authManagement.isUserPharmacySupervision() && u.IdGEMP) {
          this.getBranchesByGemp(String(u.IdGEMP));
        } else if (this.userForm.value.idGEMP) {
          this.getBranchesByGemp(String(this.userForm.value.idGEMP));
        }

        const cg = this.doctorForm.get('cedulaGeneral')?.value ?? '';
        const col = this.doctorForm.get('colegiado')?.value ?? null;
        this.userForm.patchValue({
          cedula: (cg ?? '').toString().trim(),
          idColegiado: (col === null || col === undefined || col === '' || col === 0 || col === '0')
            ? null
            : Number(col)
        }, { emitEvent: false });
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

  getBranchesByGemp(idGEMP = '') {
    if (!idGEMP) idGEMP = this.userForm.get('idGEMP')?.value;
    if (!idGEMP) { this.branches = []; return; }

    this.catalogsService.getBranchesByIdGEMP(idGEMP).subscribe({
      next: (res: DefaultResponse<Branch[]>) => { this.branches = [...res.data]; },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  searchSettlements(cp: string) {
    if (!cp) { this.settlements = []; return; }
    this.catalogsService.getSettlementByPostalCode(cp).subscribe({
      next: (res: DefaultResponse<Settlement[]>) => {
        this.settlements = [...res.data];

        if (this.settlements.length === 0) {
          this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
        }

        const idAct = this.userForm.get('idAsentamiento')?.value || this.user.idAsentamiento;
        if (idAct) {
          const sel = this.settlements.find(s => s.idAsentamiento === idAct);
          if (sel) {
            this.userForm.patchValue({
              settlementSearch: sel,
              idAsentamiento: sel.idAsentamiento
            });
          }
        }
      },
      error: (err) => this.httpMsgHandler.handleMsg(err)
    });
  }

  createUserForm(includeId: boolean = false): UntypedFormGroup {
    const group: any = {
      usr: [this.user.usr ?? '', [Validators.required, Validators.pattern(REGEX.user)]],
      password: [null],
      idGEMP: [this.user.idGEMP, this.showGempSelect ? [Validators.required] : []],
      idSucursal: [this.user.idSucursal, this.showSucursalSelect ? [Validators.required] : []],
      nombres: [this.user.nombres, [Validators.required, Validators.pattern(REGEX.text)]],
      primerApellido: [this.user.primerApellido, [Validators.required, Validators.pattern(REGEX.text)]],
      segundoApellido: [this.user.segundoApellido, [Validators.required, Validators.pattern(REGEX.text)]],
      idAsentamiento: [this.user.idAsentamiento],
      domicilio: [this.user.domicilio, [Validators.required]],
      movil: [this.user.movil, [Validators.required]],
      email: [this.user.email, [Validators.required, Validators.pattern(REGEX.correo)]],
      settlementSearch: [this.settlementSearch, [Validators.required]],
      cedula: [this.user?.cedula ?? '', [Validators.required, Validators.pattern(REGEX.cedula)]],
      idColegiado: [this.user?.idColegiado ?? null, [Validators.required]],
    };

    if (includeId) {
      group.idUsuario = [this.user.idUsuario];
    }

    return this.fb.group(group);
  }

  createDoctorForm(): UntypedFormGroup {
    return this.fb.group({
      idUsuario: [this.doctor.idUsuario],
      cedulaGeneral: [this.doctor.cedulaGeneral, [Validators.required, Validators.pattern(REGEX.cedula)]],
      colegiado: [this.doctor?.colegiado ?? null, [Validators.required]],
      universidad: [this.doctor.universidad, [Validators.required, Validators.pattern(REGEX.text)]],
      especialidad: [this.doctor.especialidad, [Validators.pattern(REGEX.text)]],
      cedulaEspecialidad: [this.doctor.cedulaEspecialidad ?? '', [Validators.pattern(REGEX.cedula)]],
      horario: [this.doctor.horario, [Validators.required]],
      idMedico: [this.doctor?.idMedico ? String(this.doctor.idMedico) : null],
    });
  }

  createArchivesForm(): UntypedFormGroup {
    return this.fb.group({
      idUsuario: [this.archive.idUsuario],
      imagen: [this.archive.imagen],
      firma: [this.archive.firma],
      pdf: [this.archive.pdf],
    });
  }

  mapToUserDetails(response: any): UserDetails {
    const u = new UserDetails({} as UserDetails);
    u.idUsuario = response.idUsuario;
    u.nombres = response.nombres;
    u.primerApellido = response.primerApellido;
    u.segundoApellido = response.segundoApellido;
    u.movil = response.movil;
    u.email = response.email;
    u.domicilio = response.domicilio;
    u.idAsentamiento = response.idAsentamiento;
    u.nombreAsentamiento = response.asentamiento;
    u.idCP = response.idCP;
    u.codigoPostal = response.codigoPostal;
    u.idMunicipio = response.idMunicipio;
    u.municipio = response.municipio;
    u.idGEMP = response.idGEMP;
    u.usr = response.usr;
    u.idSucursal = response.idSucursal;
    u.cedula = response.cedula ?? null;
    u.idColegiado = (response.idColegiado ?? null);

    return u;
  }

  mapToDoctor(response: any): Doctor {
    const d = new Doctor({} as Doctor);
    d.idMedico = response.idMedico;
    d.idUsuario = response.idUsuario;
    d.cedulaGeneral = response.cedulaGeneral;
    d.universidad = response.universidad;
    d.especialidad = response.especialidad;
    d.cedulaEspecialidad = response.cedulaEspecialidad;
    d.horario = response.horario;

    const colegiadoRaw = response.colegiado ?? response.idColegiado ?? null;
    d.colegiado = (colegiadoRaw === 0 || colegiadoRaw === '0' || colegiadoRaw === '' || colegiadoRaw === null)
      ? null
      : Number(colegiadoRaw);

    return d;
  }

  displayFn(settlement: Settlement): string {
    return settlement && settlement.asentamiento ? settlement.asentamiento : '';
  }

  setSelectedSettlement(value: Settlement): void {
    this.user.idAsentamiento = value ? value.idAsentamiento : undefined;
    this.userForm.patchValue({ idAsentamiento: this.user.idAsentamiento });
  }

  onSettlementBlur(): void {
    const val = this.userForm.get('settlementSearch')?.value;
    if (!val || typeof val !== 'object' || !val.idAsentamiento) {
      this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
    }
  }

  public confirmAdd(): void {
    const userData = this.userForm.getRawValue();
    const selectedSettlement = userData.settlementSearch as Settlement;

    if (!selectedSettlement || !selectedSettlement.idAsentamiento) {
      Swal.fire({
        icon: 'error',
        title: regionWord('Colonia requerida'),
        text: regionWord('Debes seleccionar una colonia de la lista para continuar.')
      }).then();
      return;
    }

    const idTipoUsuarioToSend = this.getSelectedTipoUsuarioId();

    if (!idTipoUsuarioToSend) {
      Swal.fire({
        icon: 'warning',
        title: 'Tipos de usuario',
        text: this.isRegionES
          ? 'No se encontró el tipo de usuario PARTICULAR.'
          : 'No se encontró el tipo de usuario requerido para guardar.'
      }).then();
      return;
    }

    const cedulaGeneral = (this.doctorForm.get('cedulaGeneral')?.value ?? '').toString().trim();
    const colegiado = this.doctorForm.get('colegiado')?.value;

    this.userForm.patchValue({
      cedula: cedulaGeneral,
      idColegiado: (colegiado === null || colegiado === undefined || colegiado === '' || colegiado === 0 || colegiado === '0')
        ? null
        : Number(colegiado)
    }, { emitEvent: false });

    const userDataSynced = this.userForm.getRawValue();

    userDataSynced.idAsentamiento = selectedSettlement.idAsentamiento;
    userDataSynced.idTipoUsuario = idTipoUsuarioToSend;
    delete userDataSynced.settlementSearch;

    if (!this.param && 'idUsuario' in userDataSynced) {
      delete (userDataSynced as any).idUsuario;
    }

    const rawMovil = this.userForm.get('movil')?.value;
    let movilE164 = '';
    if (typeof rawMovil === 'string') movilE164 = rawMovil;
    else if (rawMovil?.e164 || rawMovil?.e164Number) movilE164 = rawMovil.e164 || rawMovil.e164Number;
    else if (rawMovil?.number) movilE164 = rawMovil.number;
    userDataSynced.movil = (movilE164 || '').trim();

    const u = this.authManagement.userData();
    if (this.authManagement.isUserPharmacySupervision()) {
      userDataSynced.idGEMP = u.IdGEMP;
    }
    if (this.authManagement.isUserPharmacyResponsible()) {
      userDataSynced.idGEMP = u.IdGEMP;
      userDataSynced.idSucursal = u.IdSucursal;
    }

    userDataSynced.password = null;

    let lastResponse: any = null;

    this.userService.createUpdateUser(userDataSynced).pipe(
      tap((res: DefaultResponse<CreateUserResponse>) => {
        if (!this.param) {
          this.doctorForm.patchValue({ idUsuario: res.data });
          this.archiveForm.patchValue({ idUsuario: res.data });
        } else {
          if (this.idUsuarioActual) {
            this.archiveForm.patchValue({ idUsuario: this.idUsuarioActual });
          }
        }
      }),
      switchMap(() => {
        const doctorPayload: any = { ...this.doctorForm.getRawValue() };

        if (!this.param) {
          doctorPayload.idMedico = null;
        } else {
          if (!doctorPayload.idMedico || String(doctorPayload.idMedico).trim() === '') {
            doctorPayload.idMedico = this.doctor?.idMedico ?? null;
          }
        }

        return this.doctorService.createUpdateDoctor(doctorPayload);
      }),
      tap((res) => { lastResponse = res; }),
      switchMap(() => this.uploadUsuarioAssetsIfNeeded())
    ).subscribe({
      next: () => {
        if (lastResponse) this.httpMsgHandler.handleMsg(lastResponse as MsgResponse);
        this.router.navigate(['doctor/doctor-list']).then();
      },
      error: (err) => this.httpMsgHandler.handleMsg(err),
    });
  }

  private uploadUsuarioAssetsIfNeeded() {
    const idUsuario = this.archiveForm.get('idUsuario')?.value as string | null;
    if (!idUsuario) return of(null);

    const imgVal = this.archiveForm.get('imagen')?.value as any;
    const signVal = this.archiveForm.get('firma')?.value as any;

    const toPlainBase64 = (v?: string | null) => {
      if (!v) return '';
      const s = this.cleanBlobString(v);
      const i = s.indexOf('base64,');
      return i >= 0 ? s.substring(i + 'base64,'.length) : s;
    };

    const buildUpsert = (val: any, tipo: number) => {
      if (!val) return null;

      const raw = (typeof val === 'string')
        ? toPlainBase64(val)
        : toPlainBase64(val.base64 ?? val.dataUrl ?? '');

      if (!raw) return null;

      return {
        idUsuario,
        tipo,
        blobBase64: raw,
        contentType: (typeof val === 'object' && val?.contentType) ? val.contentType : 'image/jpeg',
        bytesSize: (typeof val === 'object' && val?.bytesSize) ? Number(val.bytesSize) : 0,
        widthPx: (typeof val === 'object' ? val?.widthPx ?? null : null),
        heightPx: (typeof val === 'object' ? val?.heightPx ?? null : null),
        hashSha256Base64: (typeof val === 'object' ? val?.hashSha256Base64 ?? null : null),
      };
    };

    const upserts = [
      buildUpsert(imgVal, 1),
      buildUpsert(signVal, 2),
    ].filter(Boolean) as any[];

    if (upserts.length === 0) return of(null);

    return of(upserts).pipe(
      switchMap(list => {
        const calls = list.map(u =>
          u.tipo === 2
            ? this.userService.uploadUsuarioAssetFirma(u)
            : this.userService.uploadUsuarioAsset(u)
        );

        return calls.length === 1
          ? calls[0]
          : calls.reduce((acc, cur) => acc.pipe(switchMap(() => cur)));
      })
    );
  }

}
